import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AgGridModule } from '@ag-grid-community/angular';
import { AlertsViewComponent } from './components/alert/component/alerts-view/alerts-view.component';
import { AlertsComponent } from './components/alert/component/alerts/alerts.component';
import { AutocompleteComponent } from './components/autocomplete/autocomplete.component';
import { BusinessSelectorModalComponent } from './components/business-selector-modal/business-selector-modal.component';
import { ButtonComponent } from './components/button/button.component';
import { CardContentComponent } from './components/card/card-content.component';
import { CardTitleComponent } from './components/card/card-title.component';
import { CardComponent } from './components/card/card.component';
import { DatePickerComponent } from './components/date-picker/date-picker.component';
import { DateRangePickerV2Component } from './components/date-range-picker/date-range-picker-v2.component';
import { EmployeeListComponent } from './components/employee-list/employee-list.component';
import { ExpansionPanelComponent } from './components/expansion-panel/expansion.component';
import { BaseInputComponent } from './components/form-elements/base/base-input/base-input.component';
import { AddressFieldsetComponent } from './components/form-elements/compound/address-fieldset/address-fieldset.component';
import { CheckboxGroupComponent } from './components/form-elements/compound/checkbox-group/checkbox-group.component';
import { StartDateEndDateComponent } from './components/form-elements/compound/start-date-end-date/start-date-end-date.component';
import { CheckboxFieldComponent } from './components/form-elements/core/checkbox-field/checkbox-field.component';
import { InputFieldComponent } from './components/form-elements/core/input-field/input-field.component';
import { RadioFieldComponent } from './components/form-elements/core/radio-field/radio-field.component';
import { SelectFieldComponent } from './components/form-elements/core/select-field/select-field.component';
import { TextareaFieldComponent } from './components/form-elements/core/textarea-field/textarea-field.component';
import { FormsPageComponent } from './components/forms-page/forms-page.component';
import { IndexPageSectionComponent } from './components/index-page/index-page-section/index-page-section.component';
import { IndexPageComponent } from './components/index-page/index-page.component';
import { LoaderSpinnerComponent } from './components/loader-spinner/loader-spinner.component';
import { ModalComponent } from './components/modal/modal.component';
import { PermissionViewerModule } from './components/permission-viewer/permission-viewer.module';
import { PopoverComponent } from './components/popover/popover.component';
import { SnackbarComponent } from './components/snackbar/snackbar.component';
import { StepperComponent } from './components/stepper/stepper.component';
import { SwaggerApiComponent } from './components/swagger-api/swagger-api.component';
import { TableActionButtonsComponent } from './components/table/table-action-buttons/table-action-buttons.component';
import { TableAgGridCheckboxComponent } from './components/table/table-ag-grid/table-ag-grid-checkbox/table-ag-grid-checkbox.component';
import { TableAgGridLinkComponent } from './components/table/table-ag-grid/table-ag-grid-link/table-ag-grid-link.component';
import { TableAgGridRadioComponent } from './components/table/table-ag-grid/table-ag-grid-radio/table-ag-grid-radio.component';
import { TableAgGridComponent } from './components/table/table-ag-grid/table-ag-grid.component';
import { TableControlComponent } from './components/table/table-control-tab/table-control-bar.component';
import { AclGroupDirective } from './directives/acl-group/acl-group.directive';
import { AngularMaterialModule } from './material-modules/angular-material.module';
import { PipesModule } from './pipes/pipes.module';
import { AdvancedSearchComponent } from './components/advanced-search/advanced-search.component';
import { SimpleAdvancedSearchComponent } from './components/simple-advanced-search/simple-advanced-search.component';
import { SimpleSearchComponent } from './components/simple-search/simple-search.component';
import { AdvancedFilterComponent } from './components/advanced-filter/advanced-filter.component';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { NumbersOnlyDirective } from './directives/numbersOnly.directive';
import { ToggleComponent } from './components/toggle/toggle.component';
import { GetDraftNameComponent } from './components/get-draft-name/get-draft-name.component';
import { PaginatorComponent } from './components/paginator/paginator.component';
import { FileDownloaderStaticComponent } from './components/file-downloader-static/file-downloader-static.component';
import { TableAgGridStatusToTextAndIconComponent } from './components/table/table-ag-grid/table-ag-grid-status-to-text-and-icon/table-ag-grid-status-to-text-and-icon.component';
import { TableAgGridMultilineComponent } from './components/table/table-ag-grid/table-ag-grid-multiline/table-ag-grid-multiline.component';
import { TableAgUpdateRequirementComponent } from './components/table/table-ag-grid/table-ag-update-requirement/table-ag-update-requirement.component';
import { TableAgHeaderHelpComponent } from './components/table/table-ag-grid/table-ag-header-help/table-ag-header-help.component';
import { TableAgHeaderActionsTooltipComponent } from './components/table/table-ag-grid/table-ag-header-actions-tooltip/table-ag-header-actions-tooltip.component';
import { TableFilterComponent } from './components/table-filter/table-filter.component';
import { TableAgGridChecknullComponent } from './components/table/table-ag-grid/table-ag-grid-checknull/table-ag-grid-checknull.component';
import { FileDownloadComponent } from './components/file-download/file-download.component';
import { BaseAddressFieldsetComponent } from './components/form-elements/compound/base-address-fieldset/base-address-fieldset.component';
import { ChipsComponent } from './components/chips/chips.component';
import { BusinessFinderPageComponent } from './components/business-finder/business-finder-page/business-finder-page.component';
import { BusinessFinderPageViewComponent } from './components/business-finder/business-finder-page-view/business-finder-page-view.component';
import { ViewDocumentComponent } from './components/view-document/view-document.component';
import { TableAgGridCallFunctionComponent } from './components/table/table-ag-grid/table-ag-grid-call-function/table-ag-grid-call-function.component';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { InfiniteRowModelModule } from '@ag-grid-community/infinite-row-model';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { MasterDetailModule } from '@ag-grid-enterprise/master-detail';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { DisableOnFormSubmitDirective } from './directives/disableOnFormSubmit.directive';
import { ValidatorChipsComponent } from './components/validator-chips/validator-chips.component';
import { LoadSpinnerWrapperComponent } from './components/load-spinner-wrapper/load-spinner-wrapper.component';
import { CarmRouterLinkDirective } from './directives/carm-router-link/carm-router-link.directive';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb/breadcrumb.component';
import { BreadcrumbViewComponent } from './components/breadcrumb/breadcrumb-view/breadcrumb-view.component';
import { DocumentListComponent } from './components/documents-list/document-list.component';
import { TabsComponent } from './components/tabs/tabs-component/tabs.component';
import { CurrencyFieldComponent } from './components/form-elements/core/currency-field/currency-field.component';
import { BackButtonGuard } from '@carm/features/private/upload-documents/guards/back-button.guard';
import { DisallowCharactersDirective } from './directives/avoid-chars.directive';
import { TelephoneIntComponent } from './components/telephone-int/tel-int-double/telephone-int.component';
import { EmailFieldComponent } from './components/form-elements/compound/email-field/email-field.component';
import { LeaveUploadGuard } from '@carm/features/private/upload-documents/guards/leave-upload.guard';
import { ConfirmTermsDialogComponent } from './components/confirm-terms-dialog/confirm-terms-dialog.component';
import { MonerisInitiateButtonComponent } from './components/moneris-initiate-button/moneris-initiate-button.component';
import { MonthPickerComponent } from './components/month-picker/month-picker.component';
import { LoaderSpinnerNoRefreshComponent } from './components/loader-spinner-no-refresh/loader-spinner-no-refresh.component';
import { ApiDownloadTermsComponent } from './components/confirm-terms-dialog/api-download-terms/api-download-terms.component';
import { OffsettingTermsComponent } from './components/confirm-terms-dialog/offsetting-terms/offsetting-terms.component';
import { DisbursmentsTermsComponent } from './components/confirm-terms-dialog/disbursments-terms/disbursments-terms.component';
import { RecourseTermsComponent } from './components/confirm-terms-dialog/recourse-terms/recourse-terms.component';
import { MdePopoverModule } from '@carm/shared/material-extended/mde/src/lib/popover';
import { InputDecimalFieldComponent } from './components/form-elements/core/input-decimal-field/input-decimal-field.component';
import { MAT_CHIPS_DEFAULT_OPTIONS } from '@angular/material/chips';
import { COMMA, SPACE } from '@angular/cdk/keycodes';
import { AlphanumericOnlyDirective } from '@carm/shared/directives/alphanumeric-only.directive';


ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  InfiniteRowModelModule,
  ServerSideRowModelModule,
  MasterDetailModule,
  RowGroupingModule
]);

@NgModule({
  declarations: [
    AclGroupDirective,
    NumbersOnlyDirective,
    DisallowCharactersDirective,
    DisableOnFormSubmitDirective,
    CarmRouterLinkDirective,
    AdvancedSearchComponent,
    SimpleAdvancedSearchComponent,
    AdvancedFilterComponent,
    TableFilterComponent,
    AlertsComponent,
    AlertsViewComponent,
    GetDraftNameComponent,
    SnackbarComponent,
    ModalComponent,
    TableControlComponent,
    TableActionButtonsComponent,
    TableAgGridComponent,
    TableAgGridCheckboxComponent,
    ButtonComponent,
    DatePickerComponent,
    DateRangePickerV2Component,
    BaseInputComponent,
    InputFieldComponent,
    TextareaFieldComponent,
    RadioFieldComponent,
    CheckboxFieldComponent,
    AddressFieldsetComponent,
    SelectFieldComponent,
    CurrencyFieldComponent,
    InputDecimalFieldComponent,
    StepperComponent,
    ExpansionPanelComponent,
    AutocompleteComponent,
    PopoverComponent,
    CheckboxGroupComponent,
    FormsPageComponent,
    IndexPageComponent,
    IndexPageSectionComponent,
    BusinessSelectorModalComponent,
    EmployeeListComponent,
    LoaderSpinnerComponent,
    CardComponent,
    CardTitleComponent,
    CardContentComponent,
    ChipsComponent,
    ValidatorChipsComponent,
    StartDateEndDateComponent,
    TableAgGridLinkComponent,
    TableAgGridRadioComponent,
    SwaggerApiComponent,
    ToggleComponent,
    TableAgGridCheckboxComponent,
    PaginatorComponent,
    TableAgGridStatusToTextAndIconComponent,
    FileDownloaderStaticComponent,
    TableAgGridMultilineComponent,
    TableAgUpdateRequirementComponent,
    TableAgHeaderHelpComponent,
    TableAgHeaderActionsTooltipComponent,
    TableAgGridChecknullComponent,
    FileDownloadComponent,
    BaseAddressFieldsetComponent,
    BusinessFinderPageComponent,
    BusinessFinderPageViewComponent,
    ViewDocumentComponent,
    SimpleSearchComponent,
    TableAgGridCallFunctionComponent,
    LoadSpinnerWrapperComponent,
    DocumentListComponent,
    BreadcrumbComponent,
    BreadcrumbViewComponent,
    TabsComponent,
    TelephoneIntComponent,
    EmailFieldComponent,
    ConfirmTermsDialogComponent,
    MonerisInitiateButtonComponent,
    MonthPickerComponent,
    LoaderSpinnerNoRefreshComponent,
    ApiDownloadTermsComponent,
    OffsettingTermsComponent,
    DisbursmentsTermsComponent,
    RecourseTermsComponent,
  ],
  imports: [
    CommonModule,
    AngularMaterialModule,
    AgGridModule,
    PipesModule,
    FormsModule.withConfig({ callSetDisabledState: 'always' }),
    ReactiveFormsModule.withConfig({ callSetDisabledState: 'whenDisabledForLegacyCode' }),
    TranslateModule,
    RouterModule,
    PermissionViewerModule,
    NgxMaskPipe,
    NgxMaskDirective,
    MdePopoverModule,
    AlphanumericOnlyDirective,
  ],
  exports: [
    AgGridModule,
    AclGroupDirective,
    NumbersOnlyDirective,
    DisallowCharactersDirective,
    DisableOnFormSubmitDirective,
    CarmRouterLinkDirective,
    AdvancedSearchComponent,
    SimpleAdvancedSearchComponent,
    AdvancedFilterComponent,
    AlertsComponent,
    AlertsViewComponent,
    GetDraftNameComponent,
    TableControlComponent,
    TableActionButtonsComponent,
    TableAgGridComponent,
    TableAgGridCheckboxComponent,
    TableFilterComponent,
    SnackbarComponent,
    ModalComponent,
    PipesModule,
    ButtonComponent,
    DatePickerComponent,
    DateRangePickerV2Component,
    BaseInputComponent,
    InputFieldComponent,
    TextareaFieldComponent,
    RadioFieldComponent,
    CheckboxFieldComponent,
    AddressFieldsetComponent,
    SelectFieldComponent,
    CurrencyFieldComponent,
    InputDecimalFieldComponent,
    FormsModule,
    ReactiveFormsModule,
    AngularMaterialModule,
    StepperComponent,
    ExpansionPanelComponent,
    AutocompleteComponent,
    PopoverComponent,
    CheckboxGroupComponent,
    FormsPageComponent,
    IndexPageComponent,
    IndexPageSectionComponent,
    EmployeeListComponent,
    LoaderSpinnerComponent,
    CardComponent,
    CardTitleComponent,
    CardContentComponent,
    ChipsComponent,
    ValidatorChipsComponent,
    StartDateEndDateComponent,
    SwaggerApiComponent,
    PermissionViewerModule,
    TableAgGridLinkComponent,
    TableAgGridRadioComponent,
    ToggleComponent,
    TableAgGridCheckboxComponent,
    TableAgGridStatusToTextAndIconComponent,
    PaginatorComponent,
    FileDownloaderStaticComponent,
    FileDownloadComponent,
    BaseAddressFieldsetComponent,
    BusinessFinderPageComponent,
    BusinessFinderPageViewComponent,
    ViewDocumentComponent,
    SimpleSearchComponent,
    LoadSpinnerWrapperComponent,
    DocumentListComponent,
    BreadcrumbComponent,
    BreadcrumbViewComponent,
    TabsComponent,
    TelephoneIntComponent,
    EmailFieldComponent,
    ConfirmTermsDialogComponent,
    MonerisInitiateButtonComponent,
    MonthPickerComponent,
    LoaderSpinnerNoRefreshComponent,
    AlphanumericOnlyDirective,
  ],
  providers: [ BackButtonGuard, LeaveUploadGuard, {
    provide: MAT_CHIPS_DEFAULT_OPTIONS,
    useValue: {
      separatorKeyCodes: [COMMA, SPACE]
    }
  } ],
})
export class SharedModule {}
